import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { Button, Chip, Box, CircularProgress, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  School,
  CheckCircle,
  Article,
  Sync,
  Edit,
  Refresh,
} from '@mui/icons-material';
import {
  useSnackableCourseExists,
  useFetchDeploymentStatusByEntity,
  useEntityResourcesCheck,
  useEntityDocumentationAsync,
  useSnackableCourseAsync,
  SnackableCourseExistsData,
} from '../../../hooks/useSnackableCourse';
import CourseDeploymentModal from './CourseDeploymentModal';
import SnackableCourseManager from './SnackableCourseManager';
import { EntityType, VersionScope } from '../../../types/snackableCourse';
import { CacheKey } from '../../../types/cache-keys';

interface Props {
  entityType: EntityType | string;
  entityId: number | null;
  entityName?: string;
  disabled?: boolean;
  versionScope?: VersionScope | null;
}

type ButtonMode =
  | 'loading'
  | 'generating_resources'
  | 'generating_course'
  | 'need_resources'
  | 'create_course'
  | 'edit_draft'
  | 'manage_course';

const TERMINAL_JOB_STATUSES = new Set([
  'completed',
  'failed',
  'error',
  'cancelled',
  'canceled',
]);

const ACTIVE_JOB_STATUSES = new Set([
  'pending',
  'processing',
  'queued',
  'running',
  'started',
]);

const getVersionScopeKey = (versionScope?: VersionScope | null) => {
  if (!versionScope) return 'generic';

  return JSON.stringify({
    product_id: versionScope.product_id ?? null,
    product_version_id: versionScope.product_version_id ?? null,
    product_license_id: versionScope.product_license_id ?? null,
    doc_version: versionScope.doc_version ?? null,
    deployment_scope: versionScope.deployment_scope ?? null,
    generation_scope: versionScope.generation_scope ?? null,
    version_source: versionScope.version_source ?? null,
  });
};

const resourceRelationKeysByEntityType: Partial<Record<string, CacheKey[]>> = {
  product: [
    CacheKey.ProductToResource,
  ],
  product_feature: [
    CacheKey.ProductFeatureToResource,
  ],
  product_component: [
    CacheKey.ProductComponentToResource,
  ],
  product_use_case: [
    CacheKey.ProductUseCaseToResource,
  ],
  go_to_market: [
    CacheKey.GtmSolutionToResource,
    CacheKey.GoToMarketToResource,
  ],
  solution_component: [
    CacheKey.GtmSolutionComponentToResource,
  ],
  security_use_case: [
    CacheKey.GtmSolutionUseCaseToResource,
    CacheKey.SecurityUseCaseToResource,
  ],
  security_use_case_component: [
    CacheKey.GtmSolutionUseCaseComponentToResource,
    CacheKey.SecurityUseCaseComponentToResource,
  ],
};

const getResourceRelationKeysForEntity = (entityType: string): CacheKey[] => {
  return [
    CacheKey.Resource,
    ...(resourceRelationKeysByEntityType[entityType] ?? []),
  ];
};

const queryKeyIncludesAny = (queryKey: unknown, values: unknown[]) => {
  const keyArray = Array.isArray(queryKey) ? queryKey : [queryKey];

  return keyArray.some((part) => {
    return values.some((value) => String(part) === String(value));
  });
};

const invalidateScopedSnackableQueries = async ({
                                                  queryClient,
                                                  entityType,
                                                  entityId,
                                                  versionScopeKey,
                                                }: {
  queryClient: ReturnType<typeof useQueryClient>;
  entityType: EntityType;
  entityId: number;
  versionScopeKey: string;
}) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourse] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourseExists] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourseStatus] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.EntityResourcesCheck] }),

    queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourse, entityType, entityId] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourseExists, entityType, entityId] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourseStatus, entityType, entityId] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.EntityResourcesCheck, entityType, entityId] }),

    queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourse, entityType, entityId, versionScopeKey] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourseExists, entityType, entityId, versionScopeKey] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourseStatus, entityType, entityId, versionScopeKey] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.EntityResourcesCheck, entityType, entityId, versionScopeKey] }),
  ]);
};

const invalidateEntityDataCaches = async (queryClient: ReturnType<typeof useQueryClient>) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [CacheKey.Product] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.CiscoProducts] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductFeature] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductComponent] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductUseCase] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductVersion] }),

    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolution] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionComponent] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionUseCase] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionUseCaseComponent] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GoToMarket] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SecurityUseCase] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SecurityUseCaseComponent] }),

    queryClient.invalidateQueries({ queryKey: [CacheKey.Resource] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductToResource] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductFeatureToResource] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductComponentToResource] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductUseCaseToResource] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionToResource] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionComponentToResource] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionUseCaseToResource] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionUseCaseComponentToResource] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GoToMarketToResource] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SecurityUseCaseToResource] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SecurityUseCaseComponentToResource] }),

    queryClient.invalidateQueries({ queryKey: [CacheKey.Tag] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductToTag] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductFeatureToTag] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductComponentToTag] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.ProductUseCaseToTag] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionToTag] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionComponentToTag] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionUseCaseToTag] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionUseCaseComponentToTag] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.GoToMarketToTag] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SecurityUseCaseToTag] }),
    queryClient.invalidateQueries({ queryKey: [CacheKey.SecurityUseCaseComponentToTag] }),
  ]);
};

const SnackableCourseButton: React.FC<Props> = ({
                                                  entityType,
                                                  entityId,
                                                  entityName,
                                                  disabled = false,
                                                  versionScope = null,
                                                }) => {
  const [managerOpen, setManagerOpen] = useState(false);
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [contextMenuAnchor, setContextMenuAnchor] = useState<null | HTMLElement>(null);

  const queryClient = useQueryClient();

  const resumedJobIdRef = useRef<string | null>(null);
  const resourceListenerArmedRef = useRef(false);
  const transparentReevaluationTimerRef = useRef<number | null>(null);
  const transparentReevaluationRunningRef = useRef(false);

  const resolvedEntityType = entityType as EntityType;
  const normalizedEntityType = String(entityType || '').trim();
  const versionScopeKey = useMemo(() => getVersionScopeKey(versionScope), [versionScope]);

  const existsQuery = useSnackableCourseExists(resolvedEntityType, entityId, versionScope);
  const resourcesQuery = useEntityResourcesCheck(entityType, entityId, versionScope);
  const statusQuery = useFetchDeploymentStatusByEntity(resolvedEntityType, entityId, versionScope);

  const existsData: SnackableCourseExistsData | undefined = existsQuery.data;

  const {
    submitJob: submitDocJob,
    isActive: docIsActive,
    jobStatus: docJobStatus,
  } = useEntityDocumentationAsync();

  const {
    jobStatus: courseJobStatus,
    polling: coursePolling,
    resumePolling,
    reset: resetCourseJob,
  } = useSnackableCourseAsync();

  const runTransparentReevaluation = async () => {
    if (!entityId) return;
    if (transparentReevaluationRunningRef.current) return;

    transparentReevaluationRunningRef.current = true;

    try {
      await Promise.all([
        resourcesQuery.refetch(),
        existsQuery.refetch(),
        statusQuery.refetch(),
      ]);
    } finally {
      transparentReevaluationRunningRef.current = false;
    }
  };

  const scheduleTransparentReevaluation = () => {
    if (!entityId) return;
    if (!resourceListenerArmedRef.current) return;

    if (transparentReevaluationTimerRef.current !== null) {
      window.clearTimeout(transparentReevaluationTimerRef.current);
    }

    transparentReevaluationTimerRef.current = window.setTimeout(() => {
      transparentReevaluationTimerRef.current = null;
      void runTransparentReevaluation();
    }, 300);
  };

  useEffect(() => {
    resumedJobIdRef.current = null;
    resourceListenerArmedRef.current = false;
    transparentReevaluationRunningRef.current = false;

    if (transparentReevaluationTimerRef.current !== null) {
      window.clearTimeout(transparentReevaluationTimerRef.current);
      transparentReevaluationTimerRef.current = null;
    }
  }, [entityId, resolvedEntityType, versionScopeKey]);

  useEffect(() => {
    if (!entityId) return;

    if (!existsQuery.isLoading && !resourcesQuery.isLoading && !statusQuery.isLoading) {
      const timer = window.setTimeout(() => {
        resourceListenerArmedRef.current = true;
      }, 1000);

      return () => {
        window.clearTimeout(timer);
      };
    }
  }, [
    entityId,
    existsQuery.isLoading,
    resourcesQuery.isLoading,
    statusQuery.isLoading,
    versionScopeKey,
  ]);

  useEffect(() => {
    if (!entityId) return;

    const relevantResourceKeys = getResourceRelationKeysForEntity(normalizedEntityType);

    const unsubscribe = queryClient.getQueryCache().subscribe((event: any) => {
      if (!resourceListenerArmedRef.current) return;

      const query = event?.query;
      const queryKey = query?.queryKey;

      if (!queryKey) return;

      const isRelevantResourceQuery = queryKeyIncludesAny(queryKey, relevantResourceKeys);

      if (!isRelevantResourceQuery) return;

      const actionType = String(event?.action?.type ?? event?.type ?? '');

      const isInvalidation =
        actionType.includes('invalidate') ||
        actionType === 'queryInvalidated';

      if (!isInvalidation) return;

      scheduleTransparentReevaluation();
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryClient,
    entityId,
    normalizedEntityType,
    versionScopeKey,
  ]);

  useEffect(() => {
    if (!entityId) return;

    const onResourceChanged = (event: Event) => {
      if (!resourceListenerArmedRef.current) return;

      const customEvent = event as CustomEvent<any>;
      const detail = customEvent.detail || {};

      if (detail.entityType && String(detail.entityType) !== normalizedEntityType) return;
      if (detail.entityId && Number(detail.entityId) !== Number(entityId)) return;

      scheduleTransparentReevaluation();
    };

    window.addEventListener('toolbox-resource-relations-changed', onResourceChanged);

    return () => {
      window.removeEventListener('toolbox-resource-relations-changed', onResourceChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entityId,
    normalizedEntityType,
    versionScopeKey,
  ]);

  useEffect(() => {
    return () => {
      if (transparentReevaluationTimerRef.current !== null) {
        window.clearTimeout(transparentReevaluationTimerRef.current);
        transparentReevaluationTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!entityId) return;

    const activeCourseJobId =
      existsData?.active_course_job_id ||
      existsData?.active_job_id ||
      (statusQuery.data as any)?.active_course_job_id ||
      null;

    const activeCourseJobStatus =
      existsData?.active_course_job_status ||
      existsData?.active_job_status ||
      (statusQuery.data as any)?.active_course_job_status ||
      null;

    const normalizedStatus = String(activeCourseJobStatus || '').trim().toLowerCase();
    const indicatesInProgress = ACTIVE_JOB_STATUSES.has(normalizedStatus);

    if (
      activeCourseJobId &&
      indicatesInProgress &&
      !coursePolling &&
      resumedJobIdRef.current !== activeCourseJobId
    ) {
      resumedJobIdRef.current = activeCourseJobId;
      resumePolling(activeCourseJobId);
    }

    if (!indicatesInProgress) {
      resumedJobIdRef.current = null;
    }
  }, [
    entityId,
    existsData,
    statusQuery.data,
    coursePolling,
    resumePolling,
    versionScopeKey,
  ]);

  useEffect(() => {
    if (!docJobStatus?.status || !entityId) return;

    const normalizedStatus = String(docJobStatus.status).trim().toLowerCase();

    if (TERMINAL_JOB_STATUSES.has(normalizedStatus)) {
      const finalize = async () => {
        await Promise.all([
          resourcesQuery.refetch(),
          existsQuery.refetch(),
          statusQuery.refetch(),
          invalidateScopedSnackableQueries({
            queryClient,
            entityType: resolvedEntityType,
            entityId,
            versionScopeKey,
          }),
          invalidateEntityDataCaches(queryClient),
        ]);
      };

      void finalize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    docJobStatus?.status,
    queryClient,
    resourcesQuery.refetch,
    existsQuery.refetch,
    statusQuery.refetch,
    resolvedEntityType,
    entityId,
    versionScopeKey,
  ]);

  useEffect(() => {
    if (!courseJobStatus?.status || !entityId) return;

    const normalizedStatus = String(courseJobStatus.status).trim().toLowerCase();

    if (TERMINAL_JOB_STATUSES.has(normalizedStatus)) {
      let cancelled = false;

      const finalize = async () => {
        try {
          await Promise.all([
            existsQuery.refetch(),
            statusQuery.refetch(),
            resourcesQuery.refetch(),
            invalidateScopedSnackableQueries({
              queryClient,
              entityType: resolvedEntityType,
              entityId,
              versionScopeKey,
            }),
          ]);
        } finally {
          if (!cancelled) {
            resumedJobIdRef.current = null;
            resetCourseJob();
          }
        }
      };

      void finalize();

      return () => {
        cancelled = true;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    courseJobStatus?.status,
    existsQuery.refetch,
    statusQuery.refetch,
    resourcesQuery.refetch,
    queryClient,
    resetCourseJob,
    resolvedEntityType,
    entityId,
    versionScopeKey,
  ]);

  const courseExists = existsData?.exists ?? false;
  const deploymentStatus = statusQuery.data;

  const backendReportsGeneratingResources = resourcesQuery.data?.is_generating === true;
  const isGeneratingResources = backendReportsGeneratingResources || docIsActive;

  const backendCourseStatus =
    existsData?.active_course_job_status ||
    existsData?.active_job_status ||
    (statusQuery.data as any)?.active_course_job_status;

  const normalizedBackendCourseStatus = String(backendCourseStatus || '').trim().toLowerCase();
  const backendCourseInProgress = ACTIVE_JOB_STATUSES.has(normalizedBackendCourseStatus);

  const isGeneratingCourse = coursePolling || backendCourseInProgress;

  const resourcesLoading = resourcesQuery.isLoading;
  const existsLoading = existsQuery.isLoading;
  const statusLoading = statusQuery.isLoading;

  const hasAllResources = resourcesQuery.data?.has_all_resources ?? true;

  const existingResourceCount = resourcesQuery.data?.resource_count ?? 0;
  const requiredResourceCount = resourcesQuery.data?.required_count ?? 7;

  const mode: ButtonMode = (() => {
    if (existsLoading || resourcesLoading || statusLoading) return 'loading';
    if (isGeneratingCourse) return 'generating_course';
    if (isGeneratingResources) return 'generating_resources';
    if (!hasAllResources) return 'need_resources';
    if (!courseExists) return 'create_course';

    const hasDraft =
      !!(deploymentStatus as any)?.draft_course ||
      !!(deploymentStatus as any)?.staged_course ||
      !!existsData?.staged_course;

    const hasLive = deploymentStatus?.production_deployment?.status === 'active';
    const hasStaging = deploymentStatus?.staging_deployment?.status === 'active';

    if (hasDraft && !hasLive && !hasStaging) return 'edit_draft';

    return 'manage_course';
  })();

  const handleGenerateResources = async (forceRegenerate = false, skipExistingRelationships = true) => {
    if (!entityId) return;

    try {
      await submitDocJob(entityType, entityId, versionScope, forceRegenerate, skipExistingRelationships);
    } catch (err) {
      console.error('Failed to submit resource generation job:', err);
    }
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setContextMenuAnchor(event.currentTarget);
  };

  const handleCloseContextMenu = () => {
    setContextMenuAnchor(null);
  };

  const handleForceRegenerate = async () => {
    handleCloseContextMenu();
    await handleGenerateResources(true, true);
  };

  const handleRemapRelationships = async () => {
    handleCloseContextMenu();
    await handleGenerateResources(false, false);
  };

  const handleClick = () => {
    switch (mode) {
      case 'need_resources':
        void handleGenerateResources();
        break;
      case 'create_course':
        setDeployModalOpen(true);
        break;
      case 'generating_course':
        setDeployModalOpen(true);
        break;
      case 'edit_draft':
      case 'manage_course':
        setManagerOpen(true);
        break;
      default:
        break;
    }
  };

  const courseProgressLabel = (() => {
    const currentTaskType =
      (courseJobStatus as any)?.current_task_type ||
      (courseJobStatus as any)?.currentTaskType ||
      undefined;

    const completedTasks =
      (courseJobStatus as any)?.completed_tasks ??
      (courseJobStatus as any)?.completedTasks ??
      undefined;

    const totalTasks =
      (courseJobStatus as any)?.total_tasks ??
      (courseJobStatus as any)?.totalTasks ??
      undefined;

    if (typeof completedTasks === 'number' && typeof totalTasks === 'number') {
      return `${completedTasks}/${totalTasks}`;
    }

    if (currentTaskType) {
      return `Task: ${currentTaskType}`;
    }

    return 'In Progress';
  })();

  const resourceGenerationLabel = (() => {
    const liveStatus = resourcesQuery.data?.active_job_status || docJobStatus?.status;
    const normalizedStatus = String(liveStatus || '').trim().toLowerCase();

    if (normalizedStatus === 'processing' || normalizedStatus === 'running') {
      return 'Generating Resources...';
    }

    if (normalizedStatus === 'completed') {
      return 'Finishing Resources...';
    }

    return 'Resources Queued...';
  })();

  const buttonConfig = {
    loading: {
      label: 'Checking...',
      icon: <CircularProgress size={16} />,
      variant: 'outlined' as const,
      color: 'primary' as const,
      tooltip: '',
    },
    generating_resources: {
      label: resourceGenerationLabel,
      icon: <Sync sx={{ animation: 'spin 1s linear infinite' }} />,
      variant: 'outlined' as const,
      color: 'warning' as const,
      tooltip: 'Generating required resources. Click disabled until complete.',
    },
    generating_course: {
      label: `Course Generation ${courseProgressLabel}`,
      icon: <Sync sx={{ animation: 'spin 1s linear infinite' }} />,
      variant: 'contained' as const,
      color: 'info' as const,
      tooltip: 'A course generation job is running. Click to reopen progress modal.',
    },
    need_resources: {
      label: `Generate Resources (${existingResourceCount}/${requiredResourceCount})`,
      icon: <Article />,
      variant: 'contained' as const,
      color: 'warning' as const,
      tooltip: [
        `Available resources (${existingResourceCount}/${requiredResourceCount}): ${
          resourcesQuery.data?.existing_types?.length
            ? resourcesQuery.data.existing_types.join(', ')
            : 'None'
        }`,
        `Missing resources: ${
          resourcesQuery.data?.missing_types?.length
            ? resourcesQuery.data.missing_types.join(', ')
            : 'None'
        }`,
      ].join('\n'),
    },
    create_course: {
      label: 'Create Course',
      icon: <School />,
      variant: 'contained' as const,
      color: 'secondary' as const,
      tooltip: '',
    },
    edit_draft: {
      label: 'Edit Draft',
      icon: <Edit />,
      variant: 'contained' as const,
      color: 'warning' as const,
      tooltip: 'A draft course exists. Open editor to review and modify before deployment.',
    },
    manage_course: {
      label: 'Manage Course',
      icon: <School />,
      variant: 'outlined' as const,
      color: 'primary' as const,
      tooltip: '',
    },
  }[mode];

  const renderStatusChips = () => {
    const chips: React.ReactNode[] = [];

    if (courseExists) {
      if (deploymentStatus?.production_deployment?.status === 'active') {
        chips.push(
          <Chip
            key="live"
            icon={<CheckCircle />}
            label="Live"
            size="small"
            color="success"
            sx={{ ml: 1 }}
          />
        );
      } else if (deploymentStatus?.staging_deployment?.status === 'active') {
        chips.push(
          <Chip
            key="staging"
            icon={<CheckCircle />}
            label="Staging"
            size="small"
            color="warning"
            sx={{ ml: 1 }}
          />
        );
      } else if (
        (deploymentStatus as any)?.draft_course ||
        (deploymentStatus as any)?.staged_course ||
        existsData?.staged_course
      ) {
        chips.push(
          <Chip
            key="draft"
            label="Draft"
            size="small"
            color="default"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        );
      }
    }

    return chips;
  };

  if (!entityId) return null;

  const isDisabled =
    disabled ||
    mode === 'loading' ||
    mode === 'generating_resources';

  return (
    <>
      <style>
        {`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
      </style>

      <Box display="flex" alignItems="center" flexWrap="wrap">
        <Tooltip
          title={buttonConfig.tooltip}
          arrow
          disableHoverListener={!buttonConfig.tooltip}
        >
          <span>
            <Button
              variant={buttonConfig.variant}
              color={buttonConfig.color}
              startIcon={buttonConfig.icon}
              onClick={handleClick}
              onContextMenu={handleContextMenu}
              disabled={isDisabled}
            >
              {buttonConfig.label}
            </Button>
          </span>
        </Tooltip>

        <Menu
          anchorEl={contextMenuAnchor}
          open={Boolean(contextMenuAnchor)}
          onClose={handleCloseContextMenu}
        >
          <MenuItem onClick={handleForceRegenerate} disabled={isDisabled || isGeneratingResources}>
            <ListItemIcon>
              <Refresh fontSize="small" />
            </ListItemIcon>
            <ListItemText>Regenerate All Resources</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleRemapRelationships} disabled={isDisabled || isGeneratingResources}>
            <ListItemIcon>
              <Sync fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remap Feature Relationships</ListItemText>
          </MenuItem>
        </Menu>

        {renderStatusChips()}
      </Box>

      {managerOpen && entityId && (
        <SnackableCourseManager
          open={managerOpen}
          entityType={resolvedEntityType}
          entityId={entityId}
          entityName={entityName}
          versionScope={versionScope}
          onClose={() => {
            setManagerOpen(false);
            void existsQuery.refetch();
            void statusQuery.refetch();
            void resourcesQuery.refetch();
          }}
        />
      )}

      {deployModalOpen && (
        <CourseDeploymentModal
          open={deployModalOpen}
          entityType={resolvedEntityType}
          entityId={entityId}
          entityName={entityName}
          versionScope={versionScope}
          onClose={() => {
            setDeployModalOpen(false);
          }}
          onSuccess={() => {
            setDeployModalOpen(false);
            resumedJobIdRef.current = null;
            resetCourseJob();
            void existsQuery.refetch();
            void statusQuery.refetch();
            void resourcesQuery.refetch();
            setManagerOpen(true);
          }}
        />
      )}
    </>
  );
};

export default SnackableCourseButton;